import React, { useState, useEffect } from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { LogOut, Home, Plus, List, Package, Building2, Tag, Star, Menu, X, User, Settings, Clock, CreditCard, Megaphone, MessageSquare, Store, Flag, Briefcase, Mail, Shield, BarChart3, ShoppingCart, Calendar, FileText } from 'lucide-react';
import { authApi, userProfileApi } from '../services/api';
import { UserProfile } from '../types';
import { isAdmin, isFairManager } from '../utils/rbac';
import { extractApiError } from '../utils/errorUtils';
import { useFeatureFlag } from '../hooks/useFeatureFlag';

export default function AdminLayout() {
  const navigate = useNavigate();
  const [userEmail, setUserEmail] = useState<string>('');
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  // Feature flag for vendor advertisements
  const { isEnabled: adsEnabled } = useFeatureFlag('vendor_advertisements');

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const data = await authApi.getCurrentUser();
        setUserEmail(data.email || '');
      } catch (err: unknown) {
        const apiError = extractApiError(err);
        console.error('Failed to fetch user info:', apiError);
      }
    };

    const fetchProfile = async () => {
      try {
        const profileData = await userProfileApi.getProfile();
        setProfile(profileData);
      } catch (err: unknown) {
        const apiError = extractApiError(err);
        console.error('Failed to fetch profile:', apiError);
      }
    };

    fetchUserInfo();
    fetchProfile();
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowProfileMenu(false);
      }
    };

    if (showProfileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProfileMenu]);

  const handleLogout = () => {
    authApi.logout();
    setShowProfileMenu(false);
    navigate('/admin/login');
  };

  const getUserInitials = () => {
    if (profile?.display_name) {
      return profile.display_name.charAt(0).toUpperCase();
    }
    if (userEmail) {
      return userEmail.charAt(0).toUpperCase();
    }
    return 'U';
  };

  return (
    <div className="min-h-screen bg-teal-50">
      <nav className="bg-primary text-white shadow-lg">
        <div className="w-full px-1">
          <div className="flex justify-between h-28">
            <div className="flex items-center space-x-4">
              {/* Hamburger menu button (mobile only) */}
              <button
                type="button"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 rounded-md hover:bg-primary-dark"
                aria-label="Toggle menu"
                title="Toggle menu"
              >
                {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>

              <Link to="/admin/dashboard" className="flex items-center space-x-2">
                {/* Watercolor logo matching landing page */}
                <picture>
                  <source srcSet="/logo.webp" type="image/webp" />
                  <img src="/logo.png" alt="Local Artist Fairs" className="h-28 w-auto" />
                </picture>
                <span className="text-xs sm:text-sm bg-background text-secondary px-2 py-1 rounded">
                  {isAdmin() ? 'Admin' : 'Owner'}
                </span>
              </Link>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4">
              <Link
                to="/"
                className="flex items-center space-x-1 hover:text-secondary-light transition-colors"
              >
                <Home className="h-4 w-4" />
                <span className="hidden sm:inline">View Site</span>
              </Link>

              {/* Profile Avatar Dropdown */}
              <div className="relative" ref={menuRef}>
                <button
                  type="button"
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className="flex items-center space-x-2 hover:text-secondary-light transition-colors focus:outline-none"
                  aria-label="User menu"
                  title="User menu"
                >
                  <div className="w-8 h-8 bg-teal-200 rounded-full flex items-center justify-center flex-shrink-0">
                    {profile?.avatar_url ? (
                      <img
                        src={profile.avatar_url}
                        alt="Profile"
                        className="w-8 h-8 rounded-full object-cover border-2 border-white"
                      />
                    ) : (
                      <span className="text-primary-dark font-semibold text-sm">{getUserInitials()}</span>
                    )}
                  </div>
                </button>

                {showProfileMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1 z-50">
                    <div className="px-4 py-2 border-b border-gray-200">
                      <p className="text-sm text-gray-500">Signed in as</p>
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {profile?.display_name || userEmail?.split('@')[0] || 'User'}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{userEmail}</p>
                      {isAdmin() && (
                        <p className="text-xs text-secondary font-medium mt-1">Administrator</p>
                      )}
                      {isFairManager() && !isAdmin() && (
                        <p className="text-xs text-secondary font-medium mt-1">Fair Manager</p>
                      )}
                    </div>

                    <Link
                      to="/admin/profile"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setShowProfileMenu(false)}
                    >
                      <User className="h-4 w-4 mr-2" />
                      Manage My Profile
                    </Link>
                    <Link
                      to="/admin/change-password"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setShowProfileMenu(false)}
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      Change Password
                    </Link>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-t border-gray-200"
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="w-full px-1 py-4 lg:py-8">
        <div className="flex space-x-8">
          {/* Sidebar Navigation - Desktop */}
          <div className="hidden lg:block w-64 flex-shrink-0">
            <div className="bg-white rounded-lg shadow p-4">
              <nav className="space-y-2">
                {isFairManager() && (
                  <Link
                    to="/fairmanager/dashboard"
                    className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                  >
                    <Building2 className="h-4 w-4" />
                    <span>My Fairs</span>
                  </Link>
                )}
                {isAdmin() && (
                  <>
                    <Link
                      to="/admin/dashboard"
                      className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                    >
                      <List className="h-4 w-4" />
                      <span>All Artist Fairs</span>
                    </Link>
                    <Link
                      to="/admin/vendors"
                      className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                    >
                      <Store className="h-4 w-4" />
                      <span>Manage Vendors</span>
                    </Link>
                    {/* Manage Products removed (Issue #83) - vendors manage their own products */}
                    <Link
                      to="/admin/product-categories"
                      className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                    >
                      <Tag className="h-4 w-4" />
                      <span>Product Categories</span>
                    </Link>
                    <Link
                      to="/admin/payment-types"
                      className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                    >
                      <CreditCard className="h-4 w-4" />
                      <span>Payment Types</span>
                    </Link>
                    {/* Product Units link removed (Issue #85) - product units system removed */}
                    {/* Farm Categories link removed (Issue #172) - fair categories system removed in Issue #84 */}
                    {/* Manage Users link removed (Issue #1245) - use Role Management at /admin/roles instead */}
                    <Link
                      to="/admin/fair-manager-requests"
                      className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                    >
                      <Briefcase className="h-4 w-4" />
                      <span>Fair Manager Requests</span>
                    </Link>
                    <Link
                      to="/admin/manager-applications"
                      className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                    >
                      <FileText className="h-4 w-4" />
                      <span>Fair Manager Applications</span>
                    </Link>
                    <Link
                      to="/admin/reservations"
                      className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                    >
                      <Calendar className="h-4 w-4" />
                      <span>Reservations</span>
                    </Link>
                    <Link
                      to="/admin/reviews"
                      className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                    >
                      <Star className="h-4 w-4" />
                      <span>Review Moderation</span>
                    </Link>
                    <Link
                      to="/admin/roles"
                      className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                    >
                      <Shield className="h-4 w-4" />
                      <span>Role Management</span>
                    </Link>
                    {adsEnabled && (
                      <>
                        <Link
                          to="/admin/advertisements"
                          className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                        >
                          <Megaphone className="h-4 w-4" />
                          <span>Advertisements</span>
                        </Link>
                        <Link
                          to="/admin/ad-packages"
                          className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                        >
                          <Package className="h-4 w-4" />
                          <span>Ad Packages</span>
                        </Link>
                        <Link
                          to="/admin/ad-purchases"
                          className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                        >
                          <ShoppingCart className="h-4 w-4" />
                          <span>Ad Purchases</span>
                        </Link>
                      </>
                    )}
                    <Link
                      to="/admin/feedback"
                      className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                    >
                      <MessageSquare className="h-4 w-4" />
                      <span>Feedback</span>
                    </Link>
                    <Link
                      to="/admin/featured-fairs"
                      className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                    >
                      <Star className="h-4 w-4" />
                      <span>Featured Artist Fairs</span>
                    </Link>
                    <Link
                      to="/admin/analytics"
                      className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                    >
                      <BarChart3 className="h-4 w-4" />
                      <span>Analytics Dashboard</span>
                    </Link>
                    <Link
                      to="/admin/feature-flags"
                      className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                    >
                      <Flag className="h-4 w-4" />
                      <span>Feature Flags</span>
                    </Link>
                    <Link
                      to="/admin/dev/email-templates"
                      className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                    >
                      <Mail className="h-4 w-4" />
                      <span>Email Templates</span>
                    </Link>
                    <Link
                      to="/admin/pending-fairs"
                      className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                    >
                      <Clock className="h-4 w-4" />
                      <span>Pending Submissions</span>
                    </Link>
                    <Link
                      to="/admin/fairs/new"
                      className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                    >
                      <Plus className="h-4 w-4" />
                      <span>Add New Artist Fair</span>
                    </Link>
                  </>
                )}
              </nav>
            </div>
          </div>

          {/* Mobile Sidebar Overlay (shown when hamburger is clicked) */}
          {sidebarOpen && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 bg-gray-600 bg-opacity-75 z-40 lg:hidden"
                onClick={() => setSidebarOpen(false)}
                data-testid="mobile-sidebar-backdrop"
              />

              {/* Sidebar Panel */}
              <div className="fixed inset-y-0 left-0 w-64 bg-white shadow-xl z-50 lg:hidden overflow-y-auto" data-testid="mobile-sidebar-panel">
                <div className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">Menu</h2>
                    <button
                      type="button"
                      onClick={() => setSidebarOpen(false)}
                      className="p-2 rounded-md hover:bg-gray-100"
                      aria-label="Close menu"
                      title="Close menu"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>

                  <nav className="space-y-2">
                    {isFairManager() && (
                      <Link
                        to="/fairmanager/dashboard"
                        onClick={() => setSidebarOpen(false)}
                        className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                        data-testid="mobile-my-fairs-link"
                      >
                        <Building2 className="h-4 w-4" />
                        <span>My Fairs</span>
                      </Link>
                    )}
                    {isAdmin() && (
                      <>
                        <Link
                          to="/admin/dashboard"
                          onClick={() => setSidebarOpen(false)}
                          className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                        >
                          <List className="h-4 w-4" />
                          <span>All Artist Fairs</span>
                        </Link>
                        <Link
                          to="/admin/vendors"
                          onClick={() => setSidebarOpen(false)}
                          className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                        >
                          <Store className="h-4 w-4" />
                          <span>Manage Vendors</span>
                        </Link>
                        {/* Manage Products removed (Issue #83) - vendors manage their own products */}
                        <Link
                          to="/admin/product-categories"
                          onClick={() => setSidebarOpen(false)}
                          className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                        >
                          <Tag className="h-4 w-4" />
                          <span>Product Categories</span>
                        </Link>
                        <Link
                          to="/admin/payment-types"
                          onClick={() => setSidebarOpen(false)}
                          className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                        >
                          <CreditCard className="h-4 w-4" />
                          <span>Payment Types</span>
                        </Link>
                        {/* Product Units link removed (Issue #85) - product units system removed */}
                        {/* Farm Categories link removed (Issue #172) - fair categories system removed in Issue #84 */}
                        {/* Manage Users link removed (Issue #1245) - use Role Management at /admin/roles instead */}
                        <Link
                          to="/admin/fair-manager-requests"
                          onClick={() => setSidebarOpen(false)}
                          className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                        >
                          <Briefcase className="h-4 w-4" />
                          <span>Fair Manager Requests</span>
                        </Link>
                        <Link
                          to="/admin/manager-applications"
                          onClick={() => setSidebarOpen(false)}
                          className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                        >
                          <FileText className="h-4 w-4" />
                          <span>Fair Manager Applications</span>
                        </Link>
                        <Link
                          to="/admin/reservations"
                          onClick={() => setSidebarOpen(false)}
                          className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                        >
                          <Calendar className="h-4 w-4" />
                          <span>Reservations</span>
                        </Link>
                        <Link
                          to="/admin/reviews"
                          onClick={() => setSidebarOpen(false)}
                          className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                        >
                          <Star className="h-4 w-4" />
                          <span>Review Moderation</span>
                        </Link>
                        <Link
                          to="/admin/roles"
                          onClick={() => setSidebarOpen(false)}
                          className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                        >
                          <Shield className="h-4 w-4" />
                          <span>Role Management</span>
                        </Link>
                        {adsEnabled && (
                          <>
                            <Link
                              to="/admin/advertisements"
                              onClick={() => setSidebarOpen(false)}
                              className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                            >
                              <Megaphone className="h-4 w-4" />
                              <span>Advertisements</span>
                            </Link>
                            <Link
                              to="/admin/ad-packages"
                              onClick={() => setSidebarOpen(false)}
                              className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                            >
                              <Package className="h-4 w-4" />
                              <span>Ad Packages</span>
                            </Link>
                            <Link
                              to="/admin/ad-purchases"
                              onClick={() => setSidebarOpen(false)}
                              className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                            >
                              <ShoppingCart className="h-4 w-4" />
                              <span>Ad Purchases</span>
                            </Link>
                          </>
                        )}
                        <Link
                          to="/admin/feedback"
                          onClick={() => setSidebarOpen(false)}
                          className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                        >
                          <MessageSquare className="h-4 w-4" />
                          <span>Feedback</span>
                        </Link>
                        <Link
                          to="/admin/featured-fairs"
                          onClick={() => setSidebarOpen(false)}
                          className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                        >
                          <Star className="h-4 w-4" />
                          <span>Featured Artist Fairs</span>
                        </Link>
                        <Link
                          to="/admin/analytics"
                          onClick={() => setSidebarOpen(false)}
                          className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                        >
                          <BarChart3 className="h-4 w-4" />
                          <span>Analytics Dashboard</span>
                        </Link>
                        <Link
                          to="/admin/feature-flags"
                          onClick={() => setSidebarOpen(false)}
                          className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                        >
                          <Flag className="h-4 w-4" />
                          <span>Feature Flags</span>
                        </Link>
                        <Link
                          to="/admin/dev/email-templates"
                          onClick={() => setSidebarOpen(false)}
                          className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                        >
                          <Mail className="h-4 w-4" />
                          <span>Email Templates</span>
                        </Link>
                        <Link
                          to="/admin/pending-fairs"
                          onClick={() => setSidebarOpen(false)}
                          className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                        >
                          <Clock className="h-4 w-4" />
                          <span>Pending Submissions</span>
                        </Link>
                        <Link
                          to="/admin/fairs/new"
                          onClick={() => setSidebarOpen(false)}
                          className="flex items-center space-x-2 p-2 rounded hover:bg-gray-100"
                        >
                          <Plus className="h-4 w-4" />
                          <span>Add New Artist Fair</span>
                        </Link>
                      </>
                    )}
                  </nav>
                </div>
              </div>
            </>
          )}

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
}